import type {ReactElement} from 'react';
import {useState} from 'react';

import {Button} from '@/components';
import {invoiceService} from '@/services/invoiceService';

import type {InvoiceAmountFilter} from './InvoiceFormModal';
import InvoiceFormModal from './InvoiceFormModal';

/**
 * The fee-invoice amount: the fee overdrafts the merchant drew over the period
 * the invoice covers, which is what the invoice collects. Uses the modal's
 * generic fetcher, so another invoice kind can pass its own instead.
 */
const fetchAmount = async ({
  businessId,
  currency,
  fromDate,
  toDate,
}: InvoiceAmountFilter): Promise<{amount: number; currency: string}> => {
  const total = await invoiceService.getOverdraftTotal({
    businessId,
    ...(currency ? {currency} : {}),
    ...(fromDate ? {fromDate} : {}),
    ...(toDate ? {toDate} : {}),
  });
  return {amount: total.amount, currency: total.currency};
};

type InvoiceCreateButtonProps = {
  /** Called after a successful create so the list can refresh in place. */
  onSuccess?: () => void | Promise<void>;
  /**
   * Set when raising from a merchant's own page, so the form fills in and locks
   * that business.
   */
  lockedBusinessId?: string;
  label?: string;
  /** Test hook for the button; `Button` suffixes it with `-button`. */
  dataCy?: string;
  /** Test hook prefix for the modal's fields. */
  formDataCy?: string;
};

const InvoiceCreateButton = ({
  onSuccess,
  lockedBusinessId,
  label = 'Create Invoice +',
  dataCy,
  formDataCy,
}: InvoiceCreateButtonProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  return isOpen ? (
    <InvoiceFormModal
      isOpen={isOpen}
      onClose={toggle}
      onSuccess={onSuccess}
      lockedBusinessId={lockedBusinessId}
      fetchAmount={fetchAmount}
      formDataCy={formDataCy}
    />
  ) : (
    <Button variant="solid" colorScheme="cyan" onClick={toggle} dataCy={dataCy}>
      {label}
    </Button>
  );
};

export default InvoiceCreateButton;
